import type { ManagedItem, StockRoom } from './shop'

/**
 * THE ITEM EDITOR'S SPEC SHEET — one summary line per section (REC-65).
 *
 * David, 4 Oct: "There is a lot of scrolling … we should have collapsible
 * sections." A collapsed section has to say its state, or collapsing it just
 * moves the scrolling into clicking: so every section gets one line, and the
 * collapsed tray reads as a spec sheet — is this a pre-order, where, until
 * when — without opening anything.
 *
 * Pure and English-only (the shop admin screens are not translated, hubCopy.ts).
 * Every figure is one the read already returns; nothing here is a second
 * definition of a number the database owns. Free and held are the database's
 * per-combination figures, added up for the line exactly as the list row
 * already did; "free" itself is never computed here.
 */

export type SectionKey =
  | 'details'
  | 'options'
  | 'pictures'
  | 'ordering'
  | 'pledges'
  | 'audience'
  | 'stock'
  | 'orders'

/** Render order in the tray. */
export const SECTIONS: { key: SectionKey; title: string }[] = [
  { key: 'details', title: 'Details & price' },
  { key: 'options', title: 'What it comes in' },
  { key: 'pictures', title: 'Pictures' },
  { key: 'ordering', title: 'Ordering' },
  { key: 'pledges', title: 'What the buyer agrees to' },
  { key: 'audience', title: 'Who it is for' },
  { key: 'stock', title: 'Stock' },
  { key: 'orders', title: 'Orders by store' },
]

export const ALL_SECTIONS: SectionKey[] = SECTIONS.map((s) => s.key)

export function isSectionKey(v: string): v is SectionKey {
  return (ALL_SECTIONS as string[]).includes(v)
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`

/** "1 Feb", or "1 Feb 2027" when it is not this year. */
export function shortDate(iso: string, now = new Date()): string {
  const d = new Date(iso)
  const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' }
  if (d.getFullYear() !== now.getFullYear()) opts.year = 'numeric'
  return d.toLocaleDateString('en-GB', opts)
}

/** "Sold at the shelf", "every store", "every store except Paris", "only at Lima". */
export type Reach = 'stock' | 'every' | 'only'

export function reachOf(item: ManagedItem): Reach {
  if (item.allowsBackorder) return 'every'
  return item.backorderExceptions.some((x) => x.allowsBackorder) ? 'only' : 'stock'
}

function names(list: string[], shown = 3): string {
  if (list.length <= shown) return list.join(', ')
  return `${list.slice(0, shown).join(', ')} +${list.length - shown}`
}

function reachWords(item: ManagedItem): string {
  const reach = reachOf(item)
  if (reach === 'stock') return 'shelf stock only'
  const differing = item.backorderExceptions
    .filter((x) => x.allowsBackorder === (reach === 'only'))
    .map((x) => x.roomName)
    .sort((a, b) => a.localeCompare(b))
  if (reach === 'every') return differing.length === 0 ? 'every store' : `every store except ${names(differing)}`
  return `only at ${names(differing)}`
}

/** A pre-order is backorder plus a close date — derived, never stored (129). */
export function isPreorder(item: ManagedItem): boolean {
  return reachOf(item) !== 'stock' && item.ordersCloseAt !== null
}

function windowWords(item: ManagedItem, now: Date): string | null {
  const o = item.ordersOpenAt
  const c = item.ordersCloseAt
  if (o && c) return `${shortDate(o, now)} – ${shortDate(c, now)}`
  if (c) return `until ${shortDate(c, now)}`
  if (o) return `from ${shortDate(o, now)}`
  return null
}

export function detailsSummary(item: ManagedItem): string {
  const owner = item.ownerRoomId ? `Local: ${item.ownerRoomName ?? 'one store'}` : 'Group item'
  return [`${item.priceStars} ★`, owner, item.isActive ? 'Listed' : 'Delisted'].join(' · ')
}

export function optionsSummary(item: ManagedItem): string {
  const live = item.variants.filter((v) => v.isActive).length
  const axes = [...item.axes].sort((a, b) => a.position - b.position).map((a) => a.label)
  if (live === 0) return 'No combinations yet'
  if (axes.length === 0) return 'Comes one way'
  return `${axes.join(' × ')} · ${plural(live, 'combination')}`
}

export function picturesSummary(item: ManagedItem): string {
  const parts: string[] = []
  if (item.image) parts.push('1 item picture')
  for (const a of [...item.axes].sort((x, y) => x.position - y.position)) {
    const n = a.options.filter((o) => o.imagePath).length
    if (n > 0) parts.push(`${n} ${a.label.toLowerCase()} ${n === 1 ? 'picture' : 'pictures'}`)
  }
  const own = item.variants.filter((v) => v.ownImage).length
  if (own > 0) parts.push(plural(own, 'combination picture'))
  return parts.length === 0 ? 'No pictures' : parts.join(' · ')
}

export function orderingSummary(item: ManagedItem, now = new Date()): string {
  const reach = reachOf(item)
  const kind = isPreorder(item) ? 'Pre-order' : reach === 'stock' ? null : 'Waiting list'
  const where = reachWords(item)
  const parts = [kind ? `${kind} · ${where}` : where.charAt(0).toUpperCase() + where.slice(1)]
  const win = windowWords(item, now)
  if (win) parts.push(win)
  parts.push(item.maxPerPerson === null ? 'no limit' : `${item.maxPerPerson} each`)
  return parts.join(' · ')
}

export function pledgesSummary(item: ManagedItem): string {
  return item.pledges.length === 0 ? 'Nothing asked' : plural(item.pledges.length, 'statement')
}

/** EMPTY MEANS EVERYONE — said in words, never as an empty list. */
export function audienceSummary(item: ManagedItem): string {
  if (item.audience.length === 0) return 'Everyone'
  const units = item.audience.filter((a) => a.orgUnitId).length
  const people = item.audience.filter((a) => a.personId).length
  return [units > 0 ? plural(units, 'unit') : null, people > 0 ? plural(people, 'person', 'people') : null]
    .filter(Boolean)
    .join(', ')
}

export function stockTotals(item: ManagedItem): { free: number; held: number; stores: number } {
  const stores = new Set<string>()
  let free = 0
  let held = 0
  for (const v of item.variants) {
    free += v.free
    held += v.held
    for (const r of v.rooms) stores.add(r.stockRoomId)
  }
  return { free, held, stores: stores.size }
}

export function stockSummary(item: ManagedItem): string {
  if (item.variants.length === 0) return 'No combinations yet'
  const t = stockTotals(item)
  if (t.stores === 0) return 'Not on any shelf yet'
  const parts = [`${t.free} free`]
  if (t.held > 0) parts.push(`${t.held} held`)
  parts.push(`in ${plural(t.stores, 'store')}`)
  return parts.join(' · ')
}

/** Per-store totals for the Stock section, which replaced the per-variant table. */
export function stockByStore(item: ManagedItem, rooms: StockRoom[]) {
  const by = new Map<string, { stockRoomId: string; roomName: string; onHand: number; held: number; free: number; closed: boolean }>()
  for (const v of item.variants) {
    for (const r of v.rooms) {
      const row = by.get(r.stockRoomId) ?? {
        stockRoomId: r.stockRoomId,
        roomName: r.roomName,
        onHand: 0,
        held: 0,
        free: 0,
        closed: rooms.some((x) => x.id === r.stockRoomId && !x.isActive),
      }
      row.onHand += r.onHand
      row.held += r.held
      row.free += r.free
      by.set(r.stockRoomId, row)
    }
  }
  return [...by.values()].sort((a, b) => a.roomName.localeCompare(b.roomName))
}

export type StatusChip = { text: string; tone: 'info' | 'warn' | 'muted' }

/**
 * The list row's chips: what a manager scans the catalogue for. Pre-order state
 * leads, because "which items are pre-orders, which are open" could not be seen
 * without opening each one (the brief's point 6).
 */
export function statusChips(item: ManagedItem, now = new Date()): StatusChip[] {
  const out: StatusChip[] = []
  const t = now.getTime()
  const opens = item.ordersOpenAt ? Date.parse(item.ordersOpenAt) : null
  const closes = item.ordersCloseAt ? Date.parse(item.ordersCloseAt) : null
  if (!item.isActive) out.push({ text: 'Delisted', tone: 'muted' })
  if (isPreorder(item)) {
    if (opens !== null && t < opens) out.push({ text: `Pre-order · opens ${shortDate(item.ordersOpenAt!, now)}`, tone: 'info' })
    else if (closes !== null && t >= closes) out.push({ text: `Pre-order closed ${shortDate(item.ordersCloseAt!, now)}`, tone: 'muted' })
    else out.push({ text: `Pre-order · closes ${shortDate(item.ordersCloseAt!, now)}`, tone: 'info' })
  } else {
    if (reachOf(item) !== 'stock') out.push({ text: 'Waiting list', tone: 'info' })
    if (opens !== null && t < opens) out.push({ text: `Opens ${shortDate(item.ordersOpenAt!, now)}`, tone: 'info' })
    else if (closes !== null) {
      out.push(t >= closes
        ? { text: `Closed ${shortDate(item.ordersCloseAt!, now)}`, tone: 'muted' }
        : { text: `Closes ${shortDate(item.ordersCloseAt!, now)}`, tone: 'info' })
    }
  }
  if (item.variants.filter((v) => v.isActive).length === 0) out.push({ text: 'No combinations', tone: 'warn' })
  if (item.audience.length > 0) out.push({ text: `For ${audienceSummary(item)}`, tone: 'muted' })
  return out
}

/**
 * ALL COLLAPSED, unless one needs action (David, 4 Oct): an item with nothing
 * to sell opens on its options; a pre-order whose window has shut opens on
 * its orders, because the supplier order is the next thing to do.
 */
export function defaultOpenSections(item: ManagedItem, now = new Date()): SectionKey[] {
  if (item.iMayEdit && item.variants.filter((v) => v.isActive).length === 0) return ['options']
  if (item.iMayEdit && isPreorder(item) && Date.parse(item.ordersCloseAt!) <= now.getTime()) return ['orders']
  return []
}

export type ItemFilter = 'all' | 'preorders' | 'delisted' | 'mine'

export const ITEM_FILTERS: { key: ItemFilter; text: string }[] = [
  { key: 'all', text: 'All' },
  { key: 'preorders', text: 'Pre-orders' },
  { key: 'delisted', text: 'Delisted' },
  { key: 'mine', text: 'Mine to edit' },
]

/** Client-side, and honest: `shop_manage_items` takes no limit, so the list is whole. */
export function filterItems(items: ManagedItem[], query: string, filter: ItemFilter): ManagedItem[] {
  const q = query.trim().toLowerCase()
  return items.filter((i) => {
    if (q && !i.name.toLowerCase().includes(q) && !i.code.toLowerCase().includes(q)) return false
    if (filter === 'preorders') return isPreorder(i)
    if (filter === 'delisted') return !i.isActive
    if (filter === 'mine') return i.iMayEdit
    return true
  })
}
